import { useEffect, useState } from 'react';
import {
  Dialog, DialogTitle, DialogContent, DialogActions, Typography, TextField, useTheme,
} from '@mui/material';
import { FormActions } from './forms';
import { useI18n } from '../../i18n/I18nProvider';
import { translateUiText } from '../../i18n/translateUiText';

interface ConfirmDialogProps {
  open: boolean;
  title?: string;
  message?: string;
  confirmLabel?: string;
  confirmColor?: 'error' | 'primary' | 'warning' | 'success';
  loading?: boolean;
  /** When set, user must type this exact text before Confirm is enabled. */
  verifyText?: string;
  verifyLabel?: string;
  onConfirm: () => void;
  onCancel: () => void;
}

export default function ConfirmDialog({
  open,
  title = 'Confirm action',
  message = 'Are you sure you want to continue? This action cannot be undone.',
  confirmLabel = 'Confirm',
  confirmColor = 'error',
  loading = false,
  verifyText,
  verifyLabel,
  onConfirm,
  onCancel,
}: ConfirmDialogProps) {
  const theme = useTheme();
  const { t } = useI18n();
  const [typed, setTyped] = useState('');

  useEffect(() => {
    if (!open) setTyped('');
  }, [open]);

  const needsVerify = Boolean(verifyText?.trim());
  const verifyOk = !needsVerify || typed.trim() === verifyText!.trim();

  return (
    <Dialog
      open={open}
      onClose={onCancel}
      maxWidth="xs"
      fullWidth
      sx={{ zIndex: (theme) => theme.zIndex.modal + 2 }}
    >
      <DialogTitle
        sx={{
          fontWeight: 650,
          letterSpacing: '-0.02em',
          borderTop: `3px solid ${theme.palette[confirmColor].main}`,
        }}
      >
        {translateUiText(title, t)}
      </DialogTitle>
      <DialogContent>
        <Typography variant="body2" color="text.secondary" sx={{ lineHeight: 1.65, whiteSpace: 'pre-line' }}>
          {translateUiText(message, t)}
        </Typography>
        {needsVerify && (
          <TextField
            autoFocus
            fullWidth
            size="small"
            margin="normal"
            label={translateUiText(verifyLabel || `Type "${verifyText}" to confirm`, t)}
            value={typed}
            onChange={(e) => setTyped(e.target.value)}
            placeholder={verifyText}
            inputProps={{ 'aria-label': 'Confirmation text' }}
          />
        )}
      </DialogContent>
      <DialogActions sx={{ borderTop: 1, borderColor: 'divider', px: 3, py: 2 }}>
        <FormActions
          onCancel={onCancel}
          onSubmit={onConfirm}
          cancelLabel="Cancel"
          submitLabel={confirmLabel}
          submitColor={confirmColor}
          loading={loading}
          disableSubmit={!verifyOk}
        />
      </DialogActions>
    </Dialog>
  );
}
